<template>
  <el-dialog v-model="visible" class="small no-header no-padding auto-height" @close="visible = false" :append-to-body="true">
    <div class="el-message-box el-message-box--center" tabindex="-1">
      <div class="el-message-box__header show-close">
        <div class="el-message-box__title"><span>{{ $t('Account Deletion Confirmation') }}</span></div>
      </div>
      <div id="el-id-3013-18" class="el-message-box__content">
        <div class="el-message-box__container">
          <div class="el-message-box__message">
            <p>{{ $t('Please select one of the currently linked accounts to receive the verification code to proceed with your account deactivation.') }}</p>
          </div>
        </div>
      </div>
      <el-radio-group v-model="authAccountType" class="account-type-radios">
        <div v-if="bindData.emailInfo?.isBind"><el-radio value="emailInfo" size="large">{{ $t('Email') }}</el-radio></div>
        <div v-if="bindData.phoneInfo?.isBind"><el-radio value="phoneInfo" size="large">{{ $t('Phone') }}</el-radio></div>
        <div v-if="bindData.googleInfo?.isBind"><el-radio value="googleInfo" size="large">{{ $t('Google') }}</el-radio></div>
        <div v-if="bindData.appleInfo?.isBind"><el-radio value="appleInfo" size="large">{{ $t('Apple ID') }}</el-radio></div>
        <div v-if="bindData.wxInfo?.isBind"><el-radio value="wxInfo" size="large">{{ $t('Wechat') }}</el-radio></div>
      </el-radio-group>
      <div class="el-message-box__btns">
        <el-button @click="visible = false">{{ $t('Cancel') }}</el-button>
        <el-button type="primary" @click="handleOpenAuth(authAccountType)">
          {{ $t('Ok') }}
        </el-button>
      </div>
    </div>
  </el-dialog>
  <AuthAccount ref="refAuthAccount" v-model="authAccountVisible" auth-type="auth" :can-change-account-type="false" @on-success="handleAuthSuccess">
    <el-button v-if="!onlyOneAuthType" type="text" class="other-auth-type" @click="handleChangeAuthType">{{ $t('Other verification methods') }}</el-button>
  </AuthAccount>
  <AuthAppleGoogle ref="refAuthAppleGoogle" auth-type="auth" @on-success="handleAuthSuccess"></AuthAppleGoogle>
  <AuthWechat ref="refAuthWechat" v-model="authWechatVisible" auth-type="auth" @on-success="handleAuthSuccess">
    <el-button v-if="!onlyOneAuthType" type="text" class="other-auth-type" @click="handleChangeAuthType">{{ $t('Other verification methods') }}</el-button>
  </AuthWechat>
</template>

<script setup>
// 该组件集合所有验证方式，用于安全验证，比如删除账号前的验证等
import { computed, reactive, ref } from 'vue';
import AuthAccount from './auth-account.vue';
import AuthAppleGoogle from './auth-apple-google.vue';
import AuthWechat from './auth-wechat.vue';
import { getBindThirdParties } from '@/api/user/user';
import { isAppleEmail } from '@/libs/tools';
import { appStore, userStore } from '@/stores';
import { filter, keys } from 'lodash';

const emits = defineEmits(['on-success'])

const visible = ref(false)

const bindData = reactive({
  appleInfo: null,
  googleInfo: null,
  wxInfo: null,
  emailInfo: null,
  phoneInfo: null,
})


const authAccountType = ref('emailInfo')

const userInfo = computed(() => userStore.userInfo)
const refAuthAccount = ref()
const refAuthAppleGoogle = ref()
const refAuthWechat = ref()

// 打开选择验证方式弹窗，或者打开对应验证方式(如果用户只绑定了一个验证方式)
const onlyOneAuthType = ref(false) // 是否只绑定了一种验证方式
const open = async () => {
  try {
    const { data } = await getBindThirdParties()
    Object.assign(bindData, data)
    bindData.emailInfo = {
      isBind: isAppleEmail(userInfo.value.email) ? false : Boolean(userInfo.value.email),
      email: isAppleEmail(userInfo.value.email) ? '' : userInfo.value.email
    }
    bindData.phoneInfo = {
      isBind: Boolean(userInfo.value.phone),
      phone: userInfo.value.phone
    }
    if (appStore.isCN) {
      bindData.googleInfo = null
    } else {
      bindData.wxInfo = null
      bindData.phone = null
    }
    const hasBindTypes = filter(keys(bindData), key => bindData[key] !== null && bindData[key]?.isBind)
    onlyOneAuthType.value = hasBindTypes.length === 1
    // 如果一个绑定方式都没有，则直接执行on-success
    if (hasBindTypes.length === 0) {
      emits('on-success', '')
      return
    }
    // 如果只有一种绑定方式，且是邮箱，手机，微信，则直接弹出验证框， 否则谷歌苹果需要先弹出选择验证方式弹窗
    if (onlyOneAuthType.value && !['apple', 'google'].includes(hasBindTypes[0])) {
      handleOpenAuth(hasBindTypes[0])
      return
    }
    authAccountType.value = hasBindTypes[0]
    visible.value = true
  } catch (e) {
    console.log(e)
  }
}

/**
 * 打开对应授权窗口
 * @param bindType bindData 对应的 key
 */
const handleOpenAuth = bindType => {
  if (bindType === 'googleInfo') {
    refAuthAppleGoogle.value.open('google')
  } else if (bindType === 'wxInfo') {
    refAuthWechat.value.open()
  } else if (bindType === 'appleInfo') {
    refAuthAppleGoogle.value.open('apple')
  } else if (bindType === 'phoneInfo') {
    refAuthAccount.value.open('phone', bindData.phoneInfo.phone)
  } else if (bindType === 'emailInfo') {
    refAuthAccount.value.open('email', bindData.emailInfo.email)
  }
  visible.value = false
}

// 切换其它验证方式
const authWechatVisible = ref(false)
const authAccountVisible = ref(false)
const handleChangeAuthType = () => {
  authWechatVisible.value = false
  authAccountVisible.value = false
  visible.value = true
}

/**
 * 验证成功
 */
const handleAuthSuccess = () => {
  emits('on-success', authAccountType.value)
}

defineExpose({
  open
})
</script>

<style lang="scss" scoped>
.account-type-radios {
  display: flex;
  flex-direction: column;
  margin-top: 24px;
  border-radius: 8px;
  overflow: hidden;
  >div {
    height: 48px;
    width: 100%;
    padding-left: 15px;
    box-sizing: border-box;
    background: var(--BG-0, #FFFFFF);
    display: flex;
    align-items: center;
    justify-content: start;
    border-bottom: 0.5px solid var(--BG-2, #EAEAEA);
    &:last-child {
      border-bottom: none;
    }
  }
}
.other-auth-type {
  display: block;
  margin: 24px auto 0;
}
</style>

<style lang="scss">
.ar-SA {
  .account-type-radios {
    >div {
      padding-left: 0;
      padding-right: 15px;
    }
  }
}
.fr-FR, .es-ES {
  .el-message-box__title {
    text-align: center;
    font-size: 16px;
  }
}
</style>